/* ASSET - assets/coco-bespoke-mode.js */

/* Bespoke mode - when the selected variant carries one of the picker block's
   bespoke option values, <product-info> gets data-coco-bespoke="true" and the
   CSS in coco-product-info.css swaps the add-to-cart stack for the buy_buttons
   block's bespoke button. The two blocks never reference each other - this
   element only stamps the common ancestor, so removing or reordering either
   block simply disables the swap without throwing.

   Updates ride PUB_SUB_EVENTS.variantChange, which VariantSelects publishes
   AFTER the Section Rendering API swap (global.js:1149), so the state survives
   the re-render. The one gap: an unavailable option combination never
   publishes, so the state then holds the last valid selection - matching how
   the rest of the PDP (price, media) behaves in that case. */

class CocoBespokeScope extends HTMLElement {
  connectedCallback() {
    this.info = this.closest('product-info')
    this.values = (this.dataset.cocoBespokeValues || '')
      .split(',')
      .map((value) => value.trim().toLowerCase())
      .filter(Boolean)
    if (!this.info || !this.values.length) return

    this.applyFromInputs()

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (!event || !event.data || !event.data.variant) return
        if (String(event.data.sectionId) !== String(this.info.dataset.section)) return

        const variant = event.data.variant
        this.setState([variant.option1, variant.option2, variant.option3])
      })
    }
  }

  disconnectedCallback() {
    if (this.unsubscribe) this.unsubscribe()
  }

  /* Initial state only - read the picker's own controls (radios or dropdown
     selects) once instead of binding to them; variantChange owns every later
     update. Covers a bespoke default variant on page load. */
  applyFromInputs() {
    const selected = []

    this.querySelectorAll('fieldset').forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked')
      if (checked) selected.push(checked.value)
    })
    this.querySelectorAll('select').forEach((select) => selected.push(select.value))

    this.setState(selected)
  }

  setState(optionValues) {
    const bespoke = optionValues
      .filter(Boolean)
      .some((value) => this.values.indexOf(String(value).trim().toLowerCase()) > -1)

    this.info.setAttribute('data-coco-bespoke', bespoke ? 'true' : 'false')
  }
}

if (!customElements.get('coco-bespoke-scope')) {
  customElements.define('coco-bespoke-scope', CocoBespokeScope)
}


/* ASSET - assets/coco-desc-clamp.js */

/* Product description clamp - <coco-desc-clamp> wraps the description block
   markup in main-product. The collapsed height is pure CSS (coco-product-info.css
   keys off data-coco-desc-clamp), so with JS missing or slow the text is
   still clamped and nothing throws. This element manages the toggle - it ships
   hidden in the markup and is revealed exclusively when the collapsed wrapper
   actually overflows (scrollHeight vs clientHeight), so a short description
   never grows a "Show more" that reveals nothing - and it drives the open and
   close animation.

   The animation is the only reason JS touches height at all. The collapsed
   value in CSS is a calc() and the expanded value is `none`, which is not a
   length and so cannot be interpolated; the element supplies the missing
   length as an inline pixel max-height for the duration of the transition and
   clears it on transitionend, handing the resting state back to the
   stylesheet.

   Under prefers-reduced-motion none of that runs. That is not politeness:
   the stylesheet drops the transition, no transitionend ever fires, and an
   inline pixel height set on expand would never be cleared.

   Re-measured on resize because the overflow point moves with width, and
   after fonts load because metrics shift when Jost swaps in. */

class CocoDescClamp extends HTMLElement {
  connectedCallback() {
    this.body = this.querySelector('[data-coco-desc-body]')
    this.toggle = this.querySelector('[data-coco-desc-toggle]')
    if (!this.body) return

    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)')

    if (this.toggle) {
      this.onToggle = () => this.setExpanded(this.getAttribute('data-coco-desc-expanded') !== 'true')
      this.toggle.addEventListener('click', this.onToggle)
    }

    /* Only max-height matters - the wrapper transitions nothing else, and a
       transition on a descendant would bubble to this same listener. */
    this.onTransitionEnd = (event) => {
      if (event.target !== this.body || event.propertyName !== 'max-height') return
      this.animating = false
      this.body.style.maxHeight = ''
      this.check()
    }
    this.body.addEventListener('transitionend', this.onTransitionEnd)

    this.onResize = () => {
      cancelAnimationFrame(this.raf)
      this.raf = requestAnimationFrame(() => this.check())
    }
    window.addEventListener('resize', this.onResize)

    this.check()
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.check())
    }
  }

  disconnectedCallback() {
    if (this.toggle && this.onToggle) this.toggle.removeEventListener('click', this.onToggle)
    if (this.body && this.onTransitionEnd) this.body.removeEventListener('transitionend', this.onTransitionEnd)
    if (this.onResize) window.removeEventListener('resize', this.onResize)
    cancelAnimationFrame(this.raf)
  }

  /* Only meaningful while collapsed - expanded content never overflows, and
     collapsing just to measure would make the page jump. While expanded the
     toggle stays visible as "Show less". Mid-animation the wrapper carries an
     inline height that is neither state, so measuring is skipped until the
     transition lands. The +1 absorbs sub-pixel rounding.

     scrollHeight vs clientHeight reads the same under max-height as it did
     under line-clamp: overflow: hidden still caps clientHeight at the
     collapsed height while scrollHeight stays the full content height. */
  check() {
    if (!this.toggle || !this.hasAttribute('data-coco-desc-clamp')) return
    if (this.animating) return
    if (this.getAttribute('data-coco-desc-expanded') === 'true') return

    this.toggle.hidden = this.body.scrollHeight <= this.body.clientHeight + 1
  }

  setExpanded(expanded) {
    const animate = this.hasAttribute('data-coco-desc-clamp') && !(this.reduced && this.reduced.matches)

    /* Expanding: pin the wrapper to the real content height first, so the
       transition has a length to run to instead of `none`. Collapsing: that
       same pixel height is the height it already has, so nothing moves - it
       just gives the transition somewhere to start from, once committed. */
    if (animate) {
      this.animating = true
      this.body.style.maxHeight = this.body.scrollHeight + 'px'
      if (!expanded) this.reflow()
    }

    this.setAttribute('data-coco-desc-expanded', expanded ? 'true' : 'false')

    /* Collapsing: hand the wrapper back to the collapsed calc() in the
       stylesheet and let it run down. Expanding keeps the inline height until
       transitionend, where it is cleared to the stylesheet's `none`. */
    if (animate && !expanded) this.body.style.maxHeight = ''

    if (this.toggle) {
      this.toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false')
      const label = expanded ? this.toggle.dataset.cocoDescLess : this.toggle.dataset.cocoDescMore
      if (label) this.toggle.textContent = label
    }

    if (!animate) {
      this.animating = false
      this.body.style.maxHeight = ''
      if (!expanded) this.check()
    }
  }

  /* Reading a layout property commits the inline height above before the
     attribute below changes it, so the browser has two values to transition
     between rather than one. */
  reflow() {
    return this.body.offsetHeight
  }
}

if (!customElements.get('coco-desc-clamp')) {
  customElements.define('coco-desc-clamp', CocoDescClamp)
}


/* ASSET - assets/coco-size-guide.js */

/* Size-guide trigger. Opens the collapsible row the variant-picker block
   points at and scrolls to it - there is no dialog any more.

   The target id is resolved in Liquid (sections/main-product.liquid, from the
   block's guide_target_row setting) and written onto the button, so nothing
   here matches on heading text and a renamed or translated row cannot break
   the link.

   Delegated from document rather than bound per button: it costs one listener,
   survives any re-render of the info column, and needs no DOMContentLoaded
   guard because the script is deferred. */

if (!window.__cocoSizeGuideBound) {
  window.__cocoSizeGuideBound = true

  document.addEventListener('click', (event) => {
    const el = event.target
    if (!(el instanceof Element)) return

    const trigger = el.closest('[data-coco-guide-target]')
    if (!trigger) return

    const target = document.getElementById(trigger.getAttribute('data-coco-guide-target'))
    if (!target) return

    event.preventDefault()
    target.open = true

    /* Offset only for a header that actually overlaps the page. Reading
       position at runtime beats hard-coding --header-height, which is only
       meaningful while the header is sticky and is rewritten on every scroll
       event (sections/header.liquid). */
    let offset = 16
    const header = document.querySelector('#shopify-section-header, .section-header, sticky-header')
    if (header) {
      const position = getComputedStyle(header).position
      if (position === 'fixed' || position === 'sticky') offset += header.offsetHeight
    }

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const top = target.getBoundingClientRect().top + window.pageYOffset - offset

    window.scrollTo({ top: top, behavior: reduce ? 'auto' : 'smooth' })

    /* Focus follows the scroll so keyboard and screen-reader users land on the
       row too; preventScroll stops the browser jumping past the smooth scroll. */
    const summary = target.querySelector('summary')
    if (summary) summary.focus({ preventScroll: true })
  })
}
